/* ──────────────────────────────────────────────────────────────────────────────
 * translationSafeDom
 *
 * Edge's and Chrome's page translators rewrite the DOM underneath React: every
 * text node they touch is re-parented into a <font> wrapper. React still holds
 * the old parent in its fiber tree, so the next time it deletes that node the
 * browser refuses:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * The throw happens inside React's commit phase, which React cannot recover
 * from — it unmounts the whole root, and the user gets a blank page.
 *
 * A scan of a translated dossier page found ~10k text nodes sitting next to
 * element siblings, so hardening them one by one is not realistic. This wraps
 * the two mutation methods React relies on instead. The native call runs first,
 * so the normal path costs nothing; only a genuine failure reaches the catch,
 * where we finish the job against the node's real parent and let React believe
 * it succeeded.
 *
 * Deliberately NOT using <meta name="google" content="notranslate">: that would
 * switch browser translation off for the whole app, and multi-language dossiers
 * are a core workflow here.
 * ────────────────────────────────────────────────────────────────────────────── */

let installed = false;

export function installTranslationSafeDom() {
  if (installed || typeof Node === 'undefined') return;
  installed = true;

  const nativeRemoveChild = Node.prototype.removeChild;
  const nativeInsertBefore = Node.prototype.insertBefore;

  // Cheap signal that the workaround is still earning its keep. Read it in the
  // console with window.__domRecoveries — a climbing number is not a new bug.
  window.__domRecoveries = { removeChild: 0, insertBefore: 0 };

  Node.prototype.removeChild = function patchedRemoveChild(child) {
    try {
      return nativeRemoveChild.call(this, child);
    } catch (error) {
      window.__domRecoveries.removeChild += 1;
      // The translator moved it — detach it from wherever it actually lives.
      if (child && child.parentNode) {
        try { nativeRemoveChild.call(child.parentNode, child); } catch (_) { /* already gone */ }
      }
      return child;
    }
  };

  Node.prototype.insertBefore = function patchedInsertBefore(node, referenceNode) {
    try {
      return nativeInsertBefore.call(this, node, referenceNode);
    } catch (error) {
      window.__domRecoveries.insertBefore += 1;
      // The reference sibling is no longer here; appending keeps React going.
      try { this.appendChild(node); } catch (_) { /* nothing sensible left to do */ }
      return node;
    }
  };
}
